import { Dayjs } from 'dayjs';
import type { DayStatus } from 'mock/13w.data';

const WEEKDAYS = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

export interface DayInfo {
  status: DayStatus;
  score: number;
  trades: number;
  grade: number | null;
  isReadyToTrade: boolean;
}

interface Props {
  current: Dayjs;
  getDayInfo: (dateStr: string) => DayInfo;
  selectedDate: string | null;
  onSelectDate: (dateStr: string) => void;
}

export function CalendarSkeleton() {
  return (
    <div className="cal-grid cal-grid--skeleton">
      <div className="cal-grid__head">
        {WEEKDAYS.map(d => (
          <div key={d} className="cal-grid__dow">{d}</div>
        ))}
      </div>
      <div className="cal-grid__body">
        {Array.from({ length: 5 }).map((_, ri) => (
          <div key={ri} className="cal-grid__row">
            {Array.from({ length: 7 }).map((_, di) => (
              <div key={di} className="cal-grid__cell cal-grid__cell--sk" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

const FUTURE_LABEL = 'Chưa tới';

export default function CalendarGrid({ current, getDayInfo, selectedDate, onSelectDate }: Props) {
  const startOfMonth = current.startOf('month');
  const daysInMonth = current.daysInMonth();
  const startDow = startOfMonth.day();

  const cells: (Dayjs | null)[] = [
    ...Array(startDow).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => startOfMonth.add(i, 'day')),
  ];

  const rows: (Dayjs | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    rows.push(cells.slice(i, i + 7).concat(Array(7).fill(null)).slice(0, 7));
  }

  return (
    <div className="cal-grid">
      <div className="cal-grid__head">
        {WEEKDAYS.map(d => <div key={d} className="cal-grid__dow">{d}</div>)}
      </div>
      <div className="cal-grid__body">
        {rows.map((row, ri) => (
          <div key={ri} className="cal-grid__row">
            {row.map((day, di) => {
              if (!day) return <div key={di} className="cal-grid__cell cal-grid__cell--empty" />;
              const dateStr = day.format('YYYY-MM-DD');
              const { status, score, trades, grade } = getDayInfo(dateStr);
              const isFuture = status === 'future';
              const isSelected = dateStr === selectedDate;

              return (
                <div
                  key={di}
                  className={`cal-grid__cell cal-grid__cell--${status}${isSelected ? ' cal-grid__cell--selected' : ''}`}
                  onClick={() => onSelectDate(dateStr)}
                >
                  <span className="cal-grid__dot" />
                  <span className="cal-grid__day">{day.date()}</span>
                  <div className="cal-grid__foot">
                    <span className="cal-grid__count">
                      {isFuture ? FUTURE_LABEL : `${grade || 0}/${score}`}
                    </span>
                    {!isFuture && (
                      <div className="cal-grid__track">
                        <div className="cal-grid__fill" style={{ width: `${((grade || 0) / score || 0 ) * 100}%` }} />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
